import { useState, useEffect, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { EducativeCarouselStyles as styledClasses } from './EducativeCarouselStyles';
import deskCarousel1 from '../../images/deskCarousel1.svg';
import deskCarousel2 from '../../images/deskCarousel2.svg';
import deskCarousel3 from '../../images/deskCarousel3.svg';
import mobCarousel1 from '../../images/mobCarousel1.svg';
import mobCarousel2 from '../../images/mobCarousel2.svg';
import mobCarousel3 from '../../images/mobCarousel3.svg';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { OPEN_BUNDLE_ACCOUNT } from '../../constants';

const EducativeCarousel = (props) => {
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { history } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const themes = useTheme();
  const isMobile = useMediaQuery(themes?.breakpoints?.down('sm'), {
    defaultMatches: true,
  });
  const { t } = useTranslation();

  const item = [
    {
      id: 1,
      content: (
        <>
          {isMobile ? (
            <Box
              component="img"
              src={mobCarousel1}
              alt={`${t('mobCarousel1', {
                defaultValue: 'mobCarousel1',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          ) : (
            <Box
              component="img"
              src={deskCarousel1}
              alt={`${t('deskCarousel1', {
                defaultValue: 'deskCarousel1',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          )}

          <Grid id="carouselContent1" container sx={styledClasses.contentContainer1}>
            <Box sx={styledClasses.contentContainer2}>
              <Typography sx={styledClasses.para1}>
                {t(OPEN_BUNDLE_ACCOUNT, {
                  defaultValue: OPEN_BUNDLE_ACCOUNT,
                })}
              </Typography>
              <Typography component="h1" sx={styledClasses.para2}>
                {t('Banking without', { defaultValue: 'Banking without' })}{' '}
                <Box component="span" sx={styledClasses.subPara2}>
                  {t('complications', { defaultValue: 'complications' })}
                </Box>
              </Typography>
              <Typography sx={styledClasses.para3}>
                {t('To protect your information, we recommend that you complete the', {
                  defaultValue: 'To protect your information, we recommend that you complete the',
                })}{' '}
                <Box component="div">
                  {t('application in a quiet, private place.', {
                    defaultValue: 'application in a quiet, private place.',
                  })}
                </Box>
              </Typography>
            </Box>
          </Grid>
        </>
      ),
    },
    {
      id: 2,
      content: (
        <>
          {isMobile ? (
            <Box
              component="img"
              src={mobCarousel2}
              alt={`${t('mobCarousel2', {
                defaultValue: 'mobCarousel2',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          ) : (
            <Box
              component="img"
              src={deskCarousel2}
              alt={`${t('deskCarousel2', {
                defaultValue: 'deskCarousel2',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          )}

          <Grid id="carouselContent2" container sx={styledClasses.contentContainer1}>
            <Box sx={styledClasses.contentContainer2}>
              <Typography sx={styledClasses.para1}>
                {t(OPEN_BUNDLE_ACCOUNT, {
                  defaultValue: OPEN_BUNDLE_ACCOUNT,
                })}
              </Typography>
              <Typography sx={styledClasses.para2}>
                {t('Keep your', { defaultValue: 'Keep your' })}{' '}
                <Box component="span" sx={styledClasses.subPara2}>
                  {t('ID nearby', { defaultValue: 'ID nearby' })}
                </Box>
              </Typography>
              <Typography sx={styledClasses.para3}>
                {t('Have your license or', { defaultValue: 'Have your license or' })}{' '}
                <Box component="div">
                  {t('passport handy.', { defaultValue: 'passport handy.' })}
                </Box>
              </Typography>
            </Box>
          </Grid>
        </>
      ),
    },
    {
      id: 3,
      content: (
        <>
          {isMobile ? (
            <Box
              component="img"
              src={mobCarousel3}
              alt={`${t('mobCarousel3', {
                defaultValue: 'mobCarousel3',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          ) : (
            <Box
              component="img"
              src={deskCarousel3}
              alt={`${t('deskCarousel3', {
                defaultValue: 'deskCarousel3',
              })} graphic`}
              sx={styledClasses.imgSize}
            />
          )}

          <Grid id="carouselContent3" container sx={styledClasses.contentContainer1}>
            <Box sx={styledClasses.contentContainer2}>
              <Typography sx={styledClasses.para1}>
                {t(OPEN_BUNDLE_ACCOUNT, {
                  defaultValue: OPEN_BUNDLE_ACCOUNT,
                })}
              </Typography>
              <Typography sx={styledClasses.para2}>
                {t('Get ready for a', { defaultValue: 'Get ready for a' })}{' '}
                <Box component="span" sx={styledClasses.subPara2}>
                  {t('selfie', { defaultValue: 'selfie' })}
                </Box>
              </Typography>
              <Typography sx={styledClasses.para3}>
                {t('For security purposes, we will need you to take a selfie to verify your', {
                  defaultValue:
                    'For security purposes, we will need you to take a selfie to verify your',
                })}{' '}
                <Box component="div">{t('identity.', { defaultValue: 'identity.' })}</Box>
              </Typography>
            </Box>
          </Grid>
        </>
      ),
    },
  ];

  useEffect(() => {
    leadGenerationContext.setCurrentPage('educative-carousel');

    const timer = setTimeout(() => {
      const element = document.scrollingElement || document.documentElement;
      requestAnimationFrame(() => {
        window.scrollTo({
          top: element.scrollHeight,
          left: 0,
          behavior: 'smooth',
        });
      });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (currentIndex === item.length - 1) {
      const timeout = setTimeout(() => {
        history.push({
          pathname: `/${leadGenerationContext.location?.toLowerCase()}/${leadGenerationContext.language?.toLowerCase()}/retail-form/${productNameURLMapping(
            leadGenerationContext.productName
          )}/contact-info`,
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, history]);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % item.length);
    }, 3000);
    return () => clearInterval(id);
  }, [currentIndex, item.length]);

  const handlerClick = (itemIndex) => {
    setCurrentIndex(itemIndex);
  };

  return (
    <div
      style={styledClasses.mainContainer}
      role="main"
      aria-describedby="carouselContent1 carouselContent2 carouselContent3"
    >
      <Box component="div">{item[currentIndex].content}</Box>
      <Box component="div" sx={styledClasses.sliderIndicator}>
        {item.map((itemContent) => (
          <Box
            component="div"
            key={itemContent.id}
            onClick={() => {
              handlerClick(itemContent.id - 1);
            }}
            onKeyDown={() => {
              handlerClick(itemContent.id - 1);
            }}
            data-testid="carousel_testid"
            sx={
              itemContent.id - 1 <= currentIndex
                ? styledClasses.sliderIndicatorItemActive
                : styledClasses.sliderIndicatorItem
            }
            aria-describedby="carouselContent1 carouselContent2 carouselContent3"
            tabIndex="0"
          />
        ))}
      </Box>
    </div>
  );
};

export default withRouter(EducativeCarousel);

EducativeCarousel.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
